'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildPeriodQueryRange, useDebounce } from '@practium/shared';
import { useGetLogMetadata, useGetLogs } from '@practium/features/api';
import type { FilterGroup } from '@practium/ui';
import { LOG_STATUS_LABELS, LogStatus } from '../types/log.types';
import type { LogAuthorFilterUser, LogDTO, LogView } from '../types/log.types';

const PAGE_SIZE = 10;
const AUTHOR_FILTER_VALUE = 'usuario:author';
const CREATED_AT_UNTIL_FILTER_VALUE = 'periodo:createdAtUntil';

const normalizeLogStatus = (status?: string | null): LogStatus => {
	if (status === LogStatus.Warn) return LogStatus.Warn;
	if (status === LogStatus.Error) return LogStatus.Error;
	return LogStatus.Info;
};

const formatLogTimestamp = (createdAt?: string | null): string => {
	if (!createdAt) return '-';
	const date = new Date(createdAt);
	if (Number.isNaN(date.getTime())) return createdAt;

	return new Intl.DateTimeFormat('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).format(date);
};

const mapLogDTOToView = (
	log: LogDTO,
	logTypeLabels: Record<string, string>,
	statusLabels: Record<string, string>
): LogView => {
	const tipoTecnico = log.logType ?? '-';
	const status = normalizeLogStatus(log.status);

	return {
		id: log._id,
		tipoTecnico,
		tipo: logTypeLabels[tipoTecnico] ?? log.typeTranslation ?? tipoTecnico,
		acao: log.log ?? '-',
		usuario: log.authorName ?? '-',
		authorId: log.authorId ?? undefined,
		timestamp: formatLogTimestamp(log.createdAt),
		status,
		statusLabel: statusLabels[status] ?? LOG_STATUS_LABELS[status] ?? status,
		navegador: log.browser ?? undefined,
		stacktrace: log.stackTrace ?? undefined,
	};
};

const getSelectedFilterValue = (values: string[], prefix: string) =>
	values.find((value) => value.startsWith(prefix))?.slice(prefix.length);

const getAuthorFilterLabel = (author: LogAuthorFilterUser | null) => {
	if (!author) return '';
	return author.email ? `${author.name || author.email} (${author.email})` : author.name;
};

export const useLogsPage = () => {
	const [searchQuery, setSearchQuery] = useState('');
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [createdAtUntil, setCreatedAtUntil] = useState('');
	const [selectedAuthor, setSelectedAuthor] = useState<LogAuthorFilterUser | null>(null);
	const [page, setPage] = useState(1);
	const [selectedLog, setSelectedLog] = useState<LogView | null>(null);
	const debouncedSearch = useDebounce(searchQuery, 400);

	const selectedType = getSelectedFilterValue(filterValues, 'tipo:');
	const selectedStatus = getSelectedFilterValue(filterValues, 'status:');
	const hasAuthorFilter = filterValues.includes(AUTHOR_FILTER_VALUE);
	const hasCreatedAtUntilFilter = filterValues.includes(CREATED_AT_UNTIL_FILTER_VALUE);
	const selectedPeriod = getSelectedFilterValue(filterValues, 'periodo:');
	const { startDate, endDate: periodEndDate } = useMemo(
		() => buildPeriodQueryRange(selectedPeriod ? [selectedPeriod] : [], customDateFrom, customDateTo),
		[selectedPeriod, customDateFrom, customDateTo]
	);
	const endDate = hasCreatedAtUntilFilter ? createdAtUntil || undefined : periodEndDate;
	const selectedAuthorId = hasAuthorFilter && selectedAuthor?.id ? selectedAuthor.id : '';

	const { data, pagination, isLoading, isFetching } = useGetLogs({
		page,
		limit: PAGE_SIZE,
		type: selectedType,
		status: selectedStatus,
		authorId: selectedAuthorId || undefined,
		startDate,
		endDate,
		search: debouncedSearch.trim() || undefined,
	});
	const { data: metadata, isLoading: isMetadataLoading } = useGetLogMetadata();

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, filterValues, customDateFrom, customDateTo, createdAtUntil, selectedAuthorId]);

	const handleFilterValuesChange = useCallback((values: string[]) => {
		setFilterValues(values);
		if (!values.includes(AUTHOR_FILTER_VALUE)) setSelectedAuthor(null);
		if (!values.includes(CREATED_AT_UNTIL_FILTER_VALUE)) setCreatedAtUntil('');
	}, []);

	const applyCustomPeriodFilter = useCallback(
		(from: string, to: string) => {
			setCustomDateFrom(from);
			setCustomDateTo(to);
		},
		[]
	);

	const applyCreatedAtUntilFilter = useCallback(
		(value: string) => {
			setCreatedAtUntil(value);
		},
		[]
	);

	const selectAuthorFilter = useCallback(
		(author: LogAuthorFilterUser) => {
			setSelectedAuthor(author);
		},
		[]
	);

	const logs = useMemo(
		() =>
			data.map((log) =>
				mapLogDTOToView(log, metadata?.logTypes ?? {}, metadata?.statuses ?? {})
			),
		[data, metadata?.logTypes, metadata?.statuses]
	);

	const filterGroups = useMemo<FilterGroup[]>(() => {
		return [
			{
				label: 'Tipo',
				selectionMode: 'single',
				searchable: true,
				options: Object.entries(metadata?.logTypes ?? {}).map(([technicalValue, label]) => ({
					value: `tipo:${technicalValue}`,
					label,
				})),
			},
			{
				label: 'Usuário',
				selectionMode: 'single',
				options: [
					{
						value: 'usuario:author',
						label: getAuthorFilterLabel(selectedAuthor) || 'Buscar usuário',
					},
				],
			},
			{
				label: 'Status',
				selectionMode: 'single',
				options: [
					{
						value: `status:${LogStatus.Info}`,
						label: metadata?.statuses[LogStatus.Info] ?? LOG_STATUS_LABELS[LogStatus.Info],
					},
					{
						value: `status:${LogStatus.Warn}`,
						label: metadata?.statuses[LogStatus.Warn] ?? LOG_STATUS_LABELS[LogStatus.Warn],
					},
					{
						value: `status:${LogStatus.Error}`,
						label: metadata?.statuses[LogStatus.Error] ?? LOG_STATUS_LABELS[LogStatus.Error],
					},
				],
			},
			{
				label: 'Período',
				selectionMode: 'single',
				options: [
					{ value: 'periodo:15', label: 'Últimos 15 dias' },
					{ value: 'periodo:30', label: 'Últimos 30 dias' },
					{ value: 'periodo:custom', label: 'Personalizado' },
					{ value: 'periodo:createdAtUntil', label: 'Criado até' },
				],
			},
		];
	}, [metadata?.logTypes, metadata?.statuses, selectedAuthor]);

	const totalPages = pagination?.totalPages ?? 1;
	const currentSelectedLog = useMemo(() => {
		if (!selectedLog) return null;
		return logs.find((log) => log.id === selectedLog.id) ?? selectedLog;
	}, [logs, selectedLog]);

	return {
		logs,
		isHealthy: metadata?.healthy,
		isMetadataLoading,
		criticalErrorsThisMonth: metadata?.criticalErrorsThisMonth,
		isLoading,
		isFetching,
		searchQuery,
		setSearchQuery: (value: string) => setSearchQuery(value.slice(0, 200)),
		filterValues,
		setFilterValues: handleFilterValuesChange,
		filterGroups,
		customDateFrom,
		customDateTo,
		applyCustomPeriodFilter,
		createdAtUntil,
		applyCreatedAtUntilFilter,
		selectedAuthorId,
		selectedAuthor,
		selectAuthorFilter,
		page,
		setPage,
		totalPages,
		selectedLog: currentSelectedLog,
		setSelectedLog,
	};
};
